import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDoctorContext } from "../../../Navigation/context/DoctorContext";
import { useToast } from "../../../Common/hooks/useToast";
import { URLS } from "../../../Common/axios_instance/axios_instance";
import { getDynamicDocumentToken } from "../../../Common/axios_instance/azure_https/azure.https";
import { DoctorDocument } from "../../../Common/interfaces/doctor.interfaces";
import { fetchDoctorDocuments } from "../https/documents_module.https";

export const useDocumentsModule = () => {
  const navigate = useNavigate();
  const { doctorProfile, isDoctorProfileLoading } = useDoctorContext();
  const { showToast } = useToast();

  const [documents, setDocuments] = useState<DoctorDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isDoctorProfileLoading) return;
    const doctorId = localStorage.getItem("doctorId");
    const doctorRole = localStorage.getItem("doctorRole");
    const isLoggedIn = !!doctorId && doctorRole === "DOCTOR";
    if (!isLoggedIn) {
      navigate("/");
      return;
    }
    if (!doctorProfile?._id) return;
    fetchDoctorDocuments(doctorProfile._id)
      .then((res: any) => setDocuments(res.data?.data || []))
      .catch(() => showToast("Failed to fetch documents", "error"))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoctorProfileLoading]);

  const handleOpenDocument = async (documentUrl: string) => {
    try {
      const parts = documentUrl.split("/");
      const docName = parts.pop();
      const docFolder = parts.join("/");
      if (!docName || !docFolder) {
        showToast("Invalid document path", "error");
        return;
      }
      // Get a short-lived token via authenticated axios call,
      // then open the public view-document URL — no cookie needed in the new tab
      const res = await getDynamicDocumentToken({ docFolder, docName });
      const token = res.data?.data?.token;
      if (!token) {
        showToast("Failed to get document token", "error");
        return;
      }
      const viewUrl = `${URLS.AZURE_URI}/signedUrl/view-document?token=${encodeURIComponent(token)}`;
      window.open(viewUrl, "_blank");
    } catch {
      showToast("Failed to open document", "error");
    }
  };

  const getDocIcon = (type: string) => {
    if (type === "license") return "badge";
    if (type === "degree") return "school";
    return "description";
  };

  const formatDocType = (type: string) => {
    if (type === "license") return "Medical License";
    if (type === "degree") return "Degree Certificate";
    if (type === "certificate") return "Other Certificate";
    return type;
  };

  return {
    documents,
    isLoading,
    handleOpenDocument,
    getDocIcon,
    formatDocType,
    navigate,
  };
};
